import React, { useState, useEffect } from 'react'

import { useInView } from 'react-cool-inview'
import { Swiper, SwiperSlide } from 'swiper/react'

import { Button, Icon, ArrowRight } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { array, string, bool } from 'prop-types'

import useGA4ItemListTracking from 'Pods/ItemPage/Hooks/useGA4ItemListTracking'
import ProductGridItem from 'Pods/Shared/ProductGridItem/ProductGridItem'

import useBreakpoint from 'Utils/useBreakpoint'

let swiperIdCounter = 0

function ProductGridArea({
  products,
  title,
  withViewAllButton = false,
  className,
  href,
  extractingCoreStyles = false,
  ariaLabel,
}) {
  const breakpoint = useBreakpoint()
  const isMobile = breakpoint ? ['xs'].includes(breakpoint) : false

  const [swiperKey] = useState(() => {
    swiperIdCounter += 1
    return `swiper-${swiperIdCounter}`
  })

  const { observe, inView } = useInView({
    rootMargin: '200px',
    unobserveOnEnter: true,
  })

  const [swiperInstance, setSwiperInstance] = useState(null)
  const [isHydrated, setIsHydrated] = useState(false)

  // Setup GA4 tracking for all product lists (improvement over legacy)
  const trackItemImpression = useGA4ItemListTracking({ products, className })

  // Handle hydration
  useEffect(() => {
    setIsHydrated(true)
  }, [])

  // ⬅️ CRITICAL - Prevents vertical scroll on horizontal swipes
  useEffect(() => {
    if (!isMobile || !swiperInstance) return

    const swiperEl = swiperInstance.el

    if (!swiperEl) {
      return
    }

    const handleTouchStart = (e) => {
      swiperEl.dataset.touchStartY = e.touches[0].pageY
    }

    const handleTouchMove = (e) => {
      const touchStartY = parseFloat(swiperEl.dataset.touchStartY || 0)
      const touchCurrentY = e.touches[0].pageY
      const touchDiffY = Math.abs(touchCurrentY - touchStartY)

      if (touchDiffY < 10) {
        e.preventDefault()
      }
    }

    swiperEl.addEventListener('touchstart', handleTouchStart, { passive: true })
    swiperEl.addEventListener('touchmove', handleTouchMove, { passive: false })

    return () => {
      swiperEl.removeEventListener('touchstart', handleTouchStart)
      swiperEl.removeEventListener('touchmove', handleTouchMove)
    }
  }, [isMobile, swiperInstance])

  const handleViewAllButtonClick = () => {
    window.open(href, '_self')
  }

  const renderProducts = () => {
    // Always render grid layout during SSR and initial hydration
    if (!isHydrated || !isMobile) {
      return (
        <div className="product-grid-area__products">
          {products.map((item, index) => (
            <ProductGridItem
              key={item.id}
              {...item}
              onVisible={trackItemImpression}
              itemIndex={trackItemImpression ? index : null}
              itemData={trackItemImpression ? item : null}
            />
          ))}
        </div>
      )
    }

    // Render Swiper only after hydration and on mobile
    return (
      <Swiper
        key={swiperKey}
        onSwiper={setSwiperInstance}
        className="product-grid-area__swiper"
        slidesPerView="auto"
        spaceBetween={12}
        grabCursor
        slidesOffsetAfter={40}
        freeMode
        watchSlidesProgress={false}
      >
        {products.map((item, index) => (
          <SwiperSlide key={item.id} className="product-grid-area__swiper-slide">
            <ProductGridItem
              {...item}
              onVisible={trackItemImpression}
              itemIndex={trackItemImpression ? index : null}
              itemData={trackItemImpression ? item : null}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    )
  }

  const renderProductGridAreaContent = () => (
    <>
      <div className="product-grid-area__header">
        <h2 className="product-grid-area__title">{title}</h2>
        {withViewAllButton && (
          <Button
            type="secondary"
            size="small"
            customClass="product-grid-area__view-all-button product-grid-area__view-all-button--desktop"
            onClick={handleViewAllButtonClick}
            aria-label={ariaLabel}
          >
            View all
          </Button>
        )}
      </div>
      {renderProducts()}
      {withViewAllButton && (
        <div className="product-grid-area__view-all">
          <Button
            type="secondary"
            size="small"
            customClass="product-grid-area__view-all-button product-grid-area__view-all-button--mobile"
            onClick={handleViewAllButtonClick}
            trailIcon={<Icon svgComponent={ArrowRight} size="small" />}
            aria-label={ariaLabel}
          >
            View all
          </Button>
        </div>
      )}
    </>
  )

  return (
    <div className={`product-grid-area ${className}`} ref={observe}>
      {(inView || extractingCoreStyles) && renderProductGridAreaContent()}
      <noscript>
        {renderProductGridAreaContent()}
      </noscript>
    </div>
  )
}

ProductGridArea.propTypes = {
  products: array.isRequired,
  title: string.isRequired,
  withViewAllButton: bool,
  className: string.isRequired,
  href: string,
  extractingCoreStyles: bool,
  ariaLabel: string.isRequired,
}

export default ProductGridArea
